/**
 * Gate screen 2/2 — teacher picker. 1:1 from the golden mock
 * (~/mockups/location-login-flow.html #view-teacher): back link, title,
 * subtitle, tappable person cards, footnote. Presentational only (Task 2.2,
 * docs/plans/2026-07-30-location-login.md).
 *
 * Teachers rostered on today's planning for the chosen group are shown
 * first; the rest of the location's planned + available staff sit behind a
 * "More teachers" disclosure — still selectable, for the floater-covering-
 * unexpectedly case.
 */

import { ChevronLeft } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PersonList } from './PersonList';
import type { LocationSessionGroup, LocationSessionTeacher, LocationTeacherToday } from '../types';

export interface TeacherPickerProps {
  locationCode: string;
  group: LocationSessionGroup;
  teachers: LocationTeacherToday[];
  /** True while the teachers-today query is in flight — distinct from a confirmed-empty roster. */
  isLoading: boolean;
  onBack: () => void;
  onPick: (teacher: LocationSessionTeacher) => void;
}

export function TeacherPicker({ locationCode, group, teachers, isLoading, onBack, onPick }: TeacherPickerProps) {
  const primary = teachers.filter((t) => t.tier === 'planned' && t.groupIds.includes(group.id));
  const rest = teachers.filter((t) => !primary.includes(t));

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-6 py-10">
      <button
        type="button"
        onClick={onBack}
        className="mb-4 flex w-full max-w-sm items-center gap-1 text-sm font-extrabold text-primary"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="mb-1 w-full max-w-sm text-2xl font-extrabold text-foreground">Who are you?</h1>
      <p className="mb-6 w-full max-w-sm text-sm text-muted-foreground">
        {/* The picked group can be at the SIBLING location (rb3↔rb5 toggle)
            — show the group's own location, not the device's home one. */}
        Teachers working at {group.locationCode.toUpperCase()} today · {group.name}
      </p>

      <div className="w-full max-w-sm">
        {isLoading ? (
          <p className="p-4 text-center text-sm text-muted-foreground">Loading today's teachers…</p>
        ) : primary.length === 0 && rest.length === 0 ? (
          <p className="rounded-2xl bg-muted p-4 text-center text-sm text-muted-foreground">
            No one is on today's planning for {locationCode.toUpperCase()} yet — ask your manager.
          </p>
        ) : (
          <>
            <PersonList teachers={primary} locationCode={locationCode} onPick={onPick} />
            {rest.length > 0 && (
              <Collapsible className="mt-2">
                <CollapsibleTrigger className="text-xs font-bold text-muted-foreground underline underline-offset-4">
                  More teachers at this location
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  <PersonList teachers={rest} locationCode={locationCode} onPick={onPick} />
                </CollapsibleContent>
              </Collapsible>
            )}
          </>
        )}
      </div>

      <p className="mt-6 max-w-sm text-center text-xs text-muted-foreground">
        Not listed? You're not on today's planning — ask your manager.
      </p>
    </div>
  );
}
